// Phase 4 slice B — personal access tokens for third-party callers
// (the MCP server, scripts, anything that is not our own client).
// See docs/PHASE_4_OPEN_API_MCP.md.
/**
 * What a token is allowed to do. Kept deliberately small: adding a scope
 * later is easy, removing one is a breaking change for every client that
 * already holds a token carrying it.
 */
export const API_TOKEN_SCOPES = {
    /** Create items from free text through the capture service. */
    capture: 'capture',
    /** Read items, boxes and the workspace. */
    itemsRead: 'items:read',
    /** Create, edit, complete and move items. */
    itemsWrite: 'items:write',
};
export const ALL_API_TOKEN_SCOPES = Object.values(API_TOKEN_SCOPES);
const KNOWN_SCOPES = new Set(ALL_API_TOKEN_SCOPES);
export const isKnownApiTokenScope = (scope) => typeof scope === 'string' && KNOWN_SCOPES.has(scope);
/**
 * The marker every token carries.
 *
 * Two jobs. It tells our own credential resolver which kind of bearer it
 * is holding without having to attempt a JWT verify and interpret the
 * failure. And it makes a leaked token recognisable — secret scanners
 * (GitHub's included) match on fixed prefixes, so a token pasted into a
 * public repo can be spotted as ours rather than looking like noise.
 */
export const API_TOKEN_PREFIX = 'tdl_';
/**
 * Bytes of randomness behind each token. 32 bytes = 256 bits, which is
 * the same order as the refresh tokens already in use and far past
 * anything brute-forceable against a hashed, indexed lookup.
 */
export const API_TOKEN_RANDOM_BYTES = 32;
/**
 * How many characters of the random part are stored in the clear so the
 * settings list can show `tdl_a1b2c3d4…` and a user can tell which token
 * is which.
 *
 * This does leak that many characters of the secret. With base64url, 8
 * characters is 48 bits — leaving ~208 bits of the 256, which is not a
 * meaningful reduction. Chosen over a separate random label because a
 * label the user has never seen cannot help them recognise the token
 * they pasted into a config file months ago.
 */
export const API_TOKEN_DISPLAY_PREFIX_LENGTH = 8;
/** Maximum length of the user-supplied token name, e.g. "Claude Desktop". */
export const API_TOKEN_NAME_MAX_LENGTH = 60;
/** How many live tokens one user may hold at once. */
export const API_TOKEN_MAX_PER_USER = 20;
/**
 * How long a new token may be minted for, in days. `null` is "never
 * expires".
 *
 * Offered rather than forced. Expiry bounds the damage from a token that
 * leaks into a dotfile repo or a screenshot, but these tokens exist to
 * drive UNATTENDED integrations — an MCP server, a script — where the
 * failure mode is silent: the thing stops working and nothing says why.
 * That is only an acceptable trade because the expiry is shown in the
 * token list and warned about by email before it happens
 * (API_TOKEN_EXPIRY_WARNING_DAYS). Remove either of those and 90 days
 * turns a rare risk into a certain quarterly breakage.
 */
export const API_TOKEN_EXPIRY_OPTION_DAYS = [90, 365, null];
/** Pre-selected in the UI. The safe default, not the only one. */
export const API_TOKEN_DEFAULT_EXPIRY_DAYS = 90;
const KNOWN_EXPIRY_DAYS = new Set(API_TOKEN_EXPIRY_OPTION_DAYS);
/**
 * Is this an expiry the server will mint? Checked server-side because
 * the value arrives in a request body, and an unrecognised one should
 * be refused rather than quietly becoming "never".
 */
export const isKnownApiTokenExpiry = (days) => days === null || (typeof days === 'number' && KNOWN_EXPIRY_DAYS.has(days));
/**
 * How many days before expiry the warning email goes out. Long enough
 * to rotate a token without a rush, short enough that the mail is still
 * relevant when it lands.
 */
export const API_TOKEN_EXPIRY_WARNING_DAYS = 7;
//# sourceMappingURL=api-token.js.map